/**
 * ChatPage (after-sale chat resolver)
 * Resolves order_id → conversation_id and redirects to the unified InquiryThreadPage,
 * which already handles countdown, closed-banner, block/redact visuals.
 * Backward-compatible URL: /orders/:orderId/chat
 */
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { getConversationByOrder } from "@/lib/modulD";
import { ArrowLeft, MessageSquare, AlertCircle, UserX } from "lucide-react";

export default function ChatPage() {
  const { t } = useTranslation();
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [error, setError] = useState(null);
  const [guestBlocked, setGuestBlocked] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user || user === false) { navigate("/login"); return; }
    (async () => {
      try {
        const data = await getConversationByOrder(orderId);
        if (data?.conversation_id) {
          navigate(`/messages/inquiry/${data.conversation_id}`, { replace: true });
        } else {
          setError(t("chat_page.conv_unavailable"));
        }
      } catch (e) {
        const detail = e?.response?.data?.detail;
        if (detail && typeof detail === "object" && detail.code === "GUEST_NOT_ALLOWED") {
          setGuestBlocked(true);
        } else {
          const msg = typeof detail === "string" ? detail : (e?.message || "Fehler beim Laden des Chats");
          setError(msg);
        }
      }
    })();
  }, [user, authLoading, orderId, navigate]);

  if (guestBlocked) {
    return (
      <div className="px-6 py-16 text-center max-w-md mx-auto">
        <UserX size={32} className="mx-auto text-[#999] mb-3" />
        <h2 className="text-base font-bold text-[#0A0A0A] mb-2">{t("chat_page.after_sale_unavailable_title")}</h2>
        <p className="text-sm text-[#666] mb-4">{t("chat_page.after_sale_unavailable_body")}</p>
        <button onClick={() => navigate(-1)} className="text-xs text-[#0A0A0A] underline">{t("chat_page.back")}</button>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-6 py-16 text-center max-w-md mx-auto">
        <AlertCircle size={32} className="mx-auto text-amber-500 mb-3" />
        <h2 className="text-base font-bold text-[#0A0A0A] mb-2">{t("chat_page.unavailable_title")}</h2>
        <p className="text-sm text-[#666] mb-4">{error}</p>
        <button onClick={() => navigate(-1)} className="text-xs text-[#0A0A0A] underline">{t("chat_page.back")}</button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center py-20 gap-3 text-[#666]">
      <MessageSquare size={28} />
      <p className="text-sm">{t("chat_page.loading")}</p>
      <div className="w-6 h-6 border-2 border-[#E5E5E5] border-t-[#0A0A0A] rounded-full animate-spin" />
    </div>
  );
}
